import {
  Cancel01Icon,
  Flag02Icon,
  InformationCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { formatToCurrency } from "shared/utils";
import { safeExternalUrl } from "shared/app/fraudAlertUtils";

const FRAUD_ALERT_ACTIVITY_TYPES = [
  "listing_fraud_alert_detected",
  "listing_fraud_alert_reported",
  "listing_fraud_alert_dismissed"
] as const;

export type FraudAlertActivityType =
  (typeof FRAUD_ALERT_ACTIVITY_TYPES)[number];

const isFraudAlertActivityType = (
  value?: string | null
): value is FraudAlertActivityType =>
  FRAUD_ALERT_ACTIVITY_TYPES.includes(value as FraudAlertActivityType);

export const fraudAlertActivityType = (
  ...candidates: (string | null | undefined)[]
): FraudAlertActivityType | null =>
  candidates.find(isFraudAlertActivityType) ?? null;

export const FRAUD_ALERT_ACTIVITY_TITLE: Record<
  FraudAlertActivityType,
  string
> = {
  listing_fraud_alert_detected: "Fraudulent copy of this listing found",
  listing_fraud_alert_reported: "Fraud alert reported",
  listing_fraud_alert_dismissed: "Fraud alert dismissed"
};

export const FRAUD_ALERT_ACTIVITY_ICON = {
  listing_fraud_alert_detected: InformationCircleIcon,
  listing_fraud_alert_reported: Flag02Icon,
  listing_fraud_alert_dismissed: Cancel01Icon
} satisfies Record<FraudAlertActivityType, unknown>;

export interface FraudAlertActivityMetadata {
  alert_id?: number;
  listing_platform?: string | null;
  location?: string | null;
  listed_for_cents?: number | null;
  listing_url?: string | null;
  property_shield_url?: string | null;
}

interface ChangeLike {
  field: string;
  new_display: string | null;
}

const fromChanges = (changes: ChangeLike[] | undefined, fields: string[]) =>
  changes?.find((change) => fields.includes(change.field))?.new_display ?? null;

interface FraudAlertActivityLine {
  label: string;
  value: string;
}

export const fraudAlertActivityLines = (
  activityType: FraudAlertActivityType,
  metadata: FraudAlertActivityMetadata | undefined,
  changes?: ChangeLike[]
): FraudAlertActivityLine[] => {
  const platform =
    metadata?.listing_platform ??
    fromChanges(changes, ["listing_platform", "platform"]);
  const lines: FraudAlertActivityLine[] = [];
  if (platform) lines.push({ label: "Listing platform", value: platform });
  if (activityType !== "listing_fraud_alert_detected") return lines;

  const location = metadata?.location ?? fromChanges(changes, ["location"]);
  if (location) lines.push({ label: "Location", value: location });

  const listedFor =
    typeof metadata?.listed_for_cents === "number"
      ? formatToCurrency({ cents: metadata.listed_for_cents, currency: "USD" })
      : fromChanges(changes, ["listed_for", "listed_for_cents"]);
  if (listedFor) lines.push({ label: "Listed for", value: listedFor });

  return lines;
};

export const fraudAlertActivityReviewUrl = (
  metadata: FraudAlertActivityMetadata | undefined,
  changes?: ChangeLike[]
) =>
  safeExternalUrl(metadata?.property_shield_url) ??
  safeExternalUrl(metadata?.listing_url) ??
  safeExternalUrl(fromChanges(changes, ["property_shield_url"])) ??
  safeExternalUrl(fromChanges(changes, ["listing_url"]));
